import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { StoreCartProvider } from "@/context/StoreCartContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import PaperPrinting from "@/pages/PaperPrinting";
import Booklet from "@/pages/Booklet";
import LargeFormat from "@/pages/LargeFormat";
import Stickers from "@/pages/Stickers";
import Settings from "@/pages/Settings";
import Apparel from "@/pages/Apparel";
import Laser from "@/pages/Laser";
import DirectPrint from "@/pages/DirectPrint";
import ChannelLetters from "@/pages/ChannelLetters";
import Sublimation from "@/pages/Sublimation";
import RollStickers from "@/pages/RollStickers";
import Catalog from "@/pages/Catalog";
import Users from "@/pages/Users";
import Quotes from "@/pages/Quotes";
import Machinery from "@/pages/Machinery";
import FixedCosts from "@/pages/FixedCosts";
import Financials from "@/pages/Financials";
import ProfitDashboard from "@/pages/ProfitDashboard";
import InkEstimator from "@/pages/InkEstimator";
import Materials from "@/pages/Materials";
import ReorderCenter from "@/pages/ReorderCenter";
import Purchases from "@/pages/Purchases";
import QuoteBuilder from "@/pages/QuoteBuilder";
import ProductsCatalog from "@/pages/ProductsCatalog";
import Storefront from "@/pages/Storefront";
import StoreProduct from "@/pages/StoreProduct";
import StoreHome from "@/pages/StoreHome";
import StoreLayout from "@/components/StoreLayout";
import Orders from "@/pages/Orders";
import PaymentReturn from "@/pages/PaymentReturn";
import Training from "@/pages/Training";
import ProductionFile from "@/pages/ProductionFile";
import PrintStudioManual from "@/pages/PrintStudioManual";
import Clients from "@/pages/Clients";
import Files from "@/pages/Files";
import Marketing from "@/pages/Marketing";
import Catalogs from "@/pages/Catalogs";
import PublicCatalog from "@/pages/PublicCatalog";
import { TourProvider } from "@/context/TourContext";

function Protected({ children, adminOnly, training }) {
  const { user, ready, tourMode } = useAuth();
  if (!ready) return <div className="p-10 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isCustomer = user.role === "client" || user.role === "reseller";
  if (user.role === "staff" && !training && !tourMode) return <Navigate to="/training" replace />;
  if (training && isCustomer) return <Navigate to="/" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  const Shell = isCustomer ? StoreLayout : Layout;
  return <Shell>{children}</Shell>;
}

function HomeRouter() {
  const { user } = useAuth();
  if (user?.role === "staff") return <Navigate to="/training" replace />;
  return (user?.role === "client" || user?.role === "reseller") ? <StoreHome /> : <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
        <StoreCartProvider>
        <BrowserRouter>
          <TourProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/c/:slug" element={<PublicCatalog />} />
            <Route path="/" element={<Protected><HomeRouter /></Protected>} />
            <Route path="/paper" element={<Protected><PaperPrinting /></Protected>} />
            <Route path="/booklet" element={<Protected><Booklet /></Protected>} />
            <Route path="/large-format" element={<Protected><LargeFormat key="lf" /></Protected>} />
            <Route path="/stickers" element={<Protected><Stickers key="stickers" /></Protected>} />
            <Route path="/apparel" element={<Protected><Apparel /></Protected>} />
            <Route path="/dtf" element={<Navigate to="/apparel" replace />} />
            <Route path="/embroidery" element={<Navigate to="/apparel" replace />} />
            <Route path="/laser" element={<Protected><Laser /></Protected>} />
            <Route path="/direct-print" element={<Protected><DirectPrint /></Protected>} />
            <Route path="/channel-letters" element={<Protected><ChannelLetters /></Protected>} />
            <Route path="/sublimation" element={<Protected><Sublimation /></Protected>} />
            <Route path="/roll-stickers" element={<Protected><RollStickers /></Protected>} />
            <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
            <Route path="/quotes" element={<Protected><Quotes /></Protected>} />
            <Route path="/quote-builder" element={<Protected><QuoteBuilder /></Protected>} />
            <Route path="/production-file" element={<Protected><ProductionFile /></Protected>} />
            <Route path="/print-studio-manual" element={<Protected training><PrintStudioManual /></Protected>} />
            <Route path="/products-catalog" element={<Protected adminOnly><ProductsCatalog /></Protected>} />
            <Route path="/store" element={<Protected><Storefront /></Protected>} />
            <Route path="/store/product/:id" element={<Protected><StoreProduct /></Protected>} />
            <Route path="/orders" element={<Protected><Orders /></Protected>} />
            <Route path="/clients" element={<Protected adminOnly><Clients /></Protected>} />
            <Route path="/files" element={<Protected adminOnly><Files /></Protected>} />
            <Route path="/marketing" element={<Protected adminOnly><Marketing /></Protected>} />
            <Route path="/catalogs" element={<Protected adminOnly><Catalogs /></Protected>} />
            <Route path="/training" element={<Protected training><Training /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentReturn /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentReturn /></Protected>} />
            <Route path="/machinery" element={<Protected adminOnly><Machinery /></Protected>} />
            <Route path="/fixed-costs" element={<Protected adminOnly><FixedCosts /></Protected>} />
            <Route path="/financials" element={<Protected adminOnly><Financials /></Protected>} />
            <Route path="/profit-dashboard" element={<Protected adminOnly><ProfitDashboard /></Protected>} />
            <Route path="/ink-estimator" element={<Protected adminOnly><InkEstimator /></Protected>} />
            <Route path="/materials" element={<Protected adminOnly><Materials /></Protected>} />
            <Route path="/reorder" element={<Protected adminOnly><ReorderCenter /></Protected>} />
            <Route path="/purchases" element={<Protected adminOnly><Purchases /></Protected>} />
            <Route path="/users" element={<Protected adminOnly><Users /></Protected>} />
            <Route path="/settings" element={<Protected adminOnly><Settings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </TourProvider>
        </BrowserRouter>
        </StoreCartProvider>
        </CartProvider>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
